import { Fragment, useState, type ReactNode } from "react";
import s from "./Ledger.module.css";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import { useDivergence, useLedgerFacts } from "../../api/queries";
import type { DivergenceQuery } from "../../api/dataSource";
import {
  comparableFirst,
  DIVERGENCE_LABEL,
  divergenceState,
  hasAuthority,
  type Divergence,
  type FactSummary,
} from "../../domain/ledger";
import { Card, EmptyState, ErrorState, Skeleton, Table } from "../../design/components";
import { t } from "../../i18n";

/**
 * Where the parties disagree, and whether it means anything.
 *
 * Storing what each party said is the easy half. The half worth paying for is
 * telling a systematic difference from noise: two parties a few minutes apart
 * either way are measuring a fuzzy event slightly differently; one party
 * consistently fifteen minutes later than another, eighteen times running, has
 * a process defect worth exactly the delay it causes.
 *
 * NOTHING ON THIS PAGE IS AVERAGED. The mean of a handler's time and an
 * airline's time is a number neither asserted and neither will defend, so what
 * is shown is the median signed difference, the spread, and how consistently
 * it points one way. And no winner is shown unless somebody agreed one — where
 * no authority is on file the page says so, because that is a contract gap
 * rather than a data gap.
 */
export function LedgerPage() {
  const { site } = useSession();
  const facts = useLedgerFacts();
  const [asked, setAsked] = useState<DivergenceQuery | null>(null);
  const divergence = useDivergence(asked);

  const compare = (f: FactSummary) => {
    if (f.partyKeys.length < 2) return;
    setAsked({ siteId: site.id, factKey: f.factKey, partyA: f.partyKeys[0], partyB: f.partyKeys[1] });
  };

  return (
    <Page eyebrow="Deep thinking" title="Where the parties disagree">
      {facts.isPending && <Skeleton rows={5} />}
      {facts.isError && <ErrorState error={facts.error} retry={() => facts.refetch()} />}
      {facts.data && facts.data.length === 0 && (
        <EmptyState>Nothing has been asserted to the ledger for this site.</EmptyState>
      )}
      {facts.data && facts.data.length > 0 && (
        <Card
          title="Facts on the ledger"
          sub="Only subjects both parties reported can diverge. The rest are one party talking."
        >
          <Table>
            <thead>
              <tr>
                <th>{t("Fact")}</th>
                <th className={s.num}>{t("Assertions")}</th>
                <th className={s.num}>{t("Comparable")}</th>
                <th>{t("Who settles it")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {comparableFirst(facts.data).map((f) => (
                <tr key={f.factKey}>
                  <td className={s.fact}>{f.factKey}</td>
                  <td className={s.num}>{f.assertions}</td>
                  {/* comparable subjects, not subjects: one party reporting is
                      the commonest state and the easiest to misread as consensus */}
                  <td className={s.num}>
                    {t("{comparable} of {subjects}", { comparable: f.comparableSubjects, subjects: f.subjects })}
                  </td>
                  <td>
                    {hasAuthority(f) ? f.authority : <span className={s.noAuthority}>{t("nobody has agreed")}</span>}
                  </td>
                  <td>
                    {f.comparableSubjects > 0 || f.partyKeys.length >= 2 ? (
                      <button type="button" onClick={() => compare(f)}>
                        {t("Compare parties")}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {asked && divergence.isPending && <Skeleton rows={4} />}
      {asked && divergence.isError && <ErrorState error={divergence.error} retry={() => divergence.refetch()} />}
      {divergence.data && <Finding d={divergence.data} />}
    </Page>
  );
}

/** A translated sentence with markup: each `{name}` is replaced by its node. */
function rich(text: string, nodes: Record<string, ReactNode>): ReactNode {
  return text
    .split(/\{(\w+)\}/)
    .map((part, i) => <Fragment key={i}>{i % 2 ? (nodes[part] ?? `{${part}}`) : part}</Fragment>);
}

function Finding({ d }: { d: Divergence }) {
  const st = divergenceState(d);
  return (
    <div
      data-testid="divergence"
      className={`${s.finding} ${st === "systematic" ? s.systematic : st === "noise" ? s.noise : s.notComparable}`}
    >
      <div className={s.eyebrow}>
        {t("{label} · {a} against {b}", { label: t(DIVERGENCE_LABEL[st]), a: d.partyA, b: d.partyB })}
      </div>

      {st === "not-comparable" ? (
        <>
          <div className={s.headline}>{t("Nothing to compare")}</div>
          <div className={s.reason}>{d.whyNot}</div>
        </>
      ) : (
        <>
          <div className={s.headline}>
            {rich(t("{minutes} minutes apart, median"), {
              minutes: <span className={s.big}>{Math.abs(d.medianMinutes ?? 0).toFixed(1)}</span>,
            })}
          </div>
          {d.finding && <div className={s.reason}>{d.finding}</div>}
          <div className={s.stats}>
            <div className={s.stat}>
              <span>{t("Paired observations")}</span>
              <b>{d.pairs}</b>
            </div>
            {/* the spread, never an average of the two parties' values */}
            <div className={s.stat}>
              <span>{t("Spread (IQR)")}</span>
              <b>{d.iqrSeconds != null ? `${Math.round(d.iqrSeconds / 60)} min` : "—"}</b>
            </div>
            <div className={s.stat}>
              <span>{t("Points one way")}</span>
              <b>{d.bLaterPct != null ? `${Math.round(Math.max(d.bLaterPct, 100 - d.bLaterPct))}%` : "—"}</b>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
